import React, { Dispatch, SetStateAction, useState, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router";
import { nextVariants } from "../../../../utils";
import {
  ContinueButton,
  Form,
  RateWrapper,
  Section,
  Text,
  FeeWrapper,
  DebitWrapper,
  Label,
} from "./style";
import CurrencySelector from "../../../CurrencySelector/CurrencySelector";
import { useAppSelector } from "../../../../../../app/hooks";
import ErrorMessage from "../../../../../../components/ErrorMessage";
import { ReactComponent as DebitIcon } from "../../../../assets/debit-icon.svg";
import { formatCurrency } from "../../../../../../utils/formatCurrency";
import { Link } from "react-router-dom";
import { Button } from "../../../../../../components/common/Button";
import {capitalizeCurrencyCode} from "../../../../../../utils/helpers";
import {formatCurrencyAmount} from "../../../../../../utils/currency";
import { InputBox } from "../../../../../../components/balances/PayoutModal/style";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { TZSPayouts } from "../../../../const";
import { FormInputs } from "../StepBeneficiary/Transfer/Local";

type SameCurrencyProps = {
  funds?: number;
  onNext?: () => void;
  sameCurrency?: string;
  transactionrate?: number;
  sendFunds?: Dispatch<SetStateAction<number>>;
  setSameCurrency?: Dispatch<SetStateAction<string>>;
  beneficiaryReceive?: number;
  rate?: number;
  transferFee?: { fees: number; currency: string };
  setlocalBankInfo?: (data) => void;
  localBankInfo?: FormInputs;
  setBankName?: (data: string) => void;
};

const SameCurrency = ({
  funds,
  onNext,
  sendFunds,
  sameCurrency,
  beneficiaryReceive,
  transactionrate,
  rate,
  transferFee,
  setlocalBankInfo,
  localBankInfo,
  setBankName
}: SameCurrencyProps) => {
  const { currencyCode } = useParams();
  const [error, setError] = useState(false);
  const { control } = useForm();
  const [currentWallet, setCurrentWallet] = useState(null);
  const [showMinimumError, setShowMinimumError] = useState<boolean>();
  const TZS = capitalizeCurrencyCode(currencyCode) === "TZS"

  const minimumPerCurrency = {
    KES: 50,
    ZAR: 100,
  };

  const minValue = minimumPerCurrency[capitalizeCurrencyCode(currencyCode)] || 100;

  const { wallets } = useAppSelector((state) => state.wallets);

  useEffect(() => {
    if (funds && funds < minValue) {
      setShowMinimumError(true);
    } else if (funds && funds > currentWallet?.balance) {
      setError(true);
      setShowMinimumError(false);
    } else {
      setShowMinimumError(false);
      setError(false);
    }
  }, [funds]);

  useEffect(() => {
    setCurrentWallet(
      wallets?.find((wallet) => wallet.currency === capitalizeCurrencyCode(currencyCode))
    );
  }, [wallets]);

  const tzsBankLookupMap = useMemo(() => {
    return TZSPayouts.reduce((acc, bank) => {
      acc[bank.value] = bank;
      return acc;
    }, {});
  }, [TZSPayouts]);
    
  const handleBankChange = (selectedBank) => {
    setlocalBankInfo({bankCode: tzsBankLookupMap[selectedBank].value})
    setBankName(tzsBankLookupMap[selectedBank].label)
  }

  const tzsFieldCheck = TZS ? localBankInfo : true

  return (
    <React.Fragment>
      <motion.div variants={nextVariants} initial="hidden" animate="visible">
        <Form>
          {TZS && 
              <InputBox>
                <Controller
                  name="bankName"
                  control={control}
                  defaultValue={localBankInfo?.bankCode}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder={`Select bank`}
                      $width="100%"
                      field={field}
                      disableLabel
                      options={TZSPayouts}
                      onChange={handleBankChange}
                      showSearch
                      filterOption={(inputValue, option) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      lightBorder={true}
                      value={field.value || localBankInfo?.bankCode}
                    />
                  )}
                />
              </InputBox>
          }
          <Section>
            <Controller
              name="amount"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <CurrencySelector
                  countries={[
                    {
                      currencyCode: capitalizeCurrencyCode(currencyCode),
                      flagCode: capitalizeCurrencyCode(currencyCode),
                    },
                  ]}
                  placeholder="Amount"
                  value={funds}
                  onValueChange={(value) => sendFunds(value)}
                  selectValue={sameCurrency}
                  arrow={true}
                  selector={true}
                />
              )}
            />

            {showMinimumError && (
              <div className="min">
                <ErrorMessage
                  message={`Minimum amount: ${capitalizeCurrencyCode(sameCurrency)} ${new Intl.NumberFormat().format(
                    minValue
                  )}`}
                />
              </div>
            )}

            {error && (
              <div className="min">
                <span>Exceeds your wallet balance.</span>
                <Link to="/wallets"> Top-up funds</Link>
              </div>
            )}
          </Section>

          {tzsFieldCheck && !!beneficiaryReceive && (
            <RateWrapper>
              <Text>
                <FeeWrapper>
                  +
                  <span>{`${transferFee?.currency ?? ''} ${transferFee?.fees}`}</span>
                </FeeWrapper>
              </Text>
              <Text>Processing fee</Text>
            </RateWrapper>
          )}

          {tzsFieldCheck && typeof transactionrate === "number" && !isNaN(rate) && (
            <RateWrapper>
              <Text>
                {transactionrate?.toLocaleString(undefined, {
                  style: "currency",
                  currency: capitalizeCurrencyCode(currencyCode),
                })}
              </Text>
              <Text>Processing fee</Text>
            </RateWrapper>
          )}

          {tzsFieldCheck && beneficiaryReceive !== null && (
            <DebitWrapper>
              <DebitIcon />
              <Section>
                <Label>Beneficiary gets</Label>
                <h3>
                  {formatCurrencyAmount(capitalizeCurrencyCode(currencyCode), beneficiaryReceive)}
                </h3>
              </Section>
            </DebitWrapper>
          )}

          <ContinueButton>
            <span className="form-label">
              {" "}
              {capitalizeCurrencyCode(currencyCode) !== "NGN" &&
                `Funds should arrive within 1-3 working days.`}
            </span>

            <Button
              label="Continue"
              height="48px"
              fontSize="14px"
              onClick={onNext}
              disabled={
                !funds ||
                !sameCurrency ||
                Number(sameCurrency) < 100 ||
                !capitalizeCurrencyCode(currencyCode) ||
                error ||
                showMinimumError ||
                !tzsFieldCheck
              }
            />
          </ContinueButton>
        </Form>
      </motion.div>
    </React.Fragment>
  );
};

export default SameCurrency;
